import { useNavigate } from 'react-router-dom';
import { FamilyTask } from '@/hooks/useTasks';
import { getSmartDateDisplay } from '@/lib/dateUtils';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { useUniversalContacts } from '@/hooks/useUniversalContacts';
import { MultipleAssigneeAvatars } from '@/components/tasks/MultipleAssigneeAvatars';
import { GripVertical } from "lucide-react";
import { useNativeConfirmation } from '@/hooks/useNativeConfirmation';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SimpleTaskItemProps {
  task: FamilyTask;
  onCompleteTask: (taskId: string) => void;
  currentUserProfileId?: string;
  onTaskCompleted?: () => void;
  familyId?: string;
  isDraggable?: boolean;
}

export function SimpleTaskItem({ 
  task, 
  onCompleteTask, 
  currentUserProfileId, 
  onTaskCompleted,
  familyId,
  isDraggable = false
}: SimpleTaskItemProps) {
  const navigate = useNavigate();
  const { contacts } = useUniversalContacts(familyId);
  const { showConfirmation } = useNativeConfirmation();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: task.id,
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  // Get assigned contacts using the new assigned_to_list or fallback to assigned_to
  const assignedProfileIds = (task as any).assigned_to_list && (task as any).assigned_to_list.length > 0 
    ? (task as any).assigned_to_list 
    : task.assigned_to ? [task.assigned_to] : [];
  const uniqueAssignedIds = Array.from(new Set(assignedProfileIds));
    
  const assignedContacts = contacts.filter(contact => {
    if (contact.source === 'family_member') {
      const profileId = contact.profile_id;
      return !!profileId && uniqueAssignedIds.includes(profileId);
    } else {
      const personId = contact.originalData?.id;
      const linkedProfileId = contact.originalData?.profile_id;
      return (personId && uniqueAssignedIds.includes(personId)) || (linkedProfileId && uniqueAssignedIds.includes(linkedProfileId));
    }
  });
  
  // Check if current user can complete this task (allow anyone to complete but require confirmation)
  const canComplete = task.status === 'pending' && currentUserProfileId;
  
  // Check if confirmation is needed (user is not assigned and not creator)
  const needsConfirmation = task.status === 'pending' && 
    currentUserProfileId &&
    !uniqueAssignedIds.includes(currentUserProfileId) &&
    task.created_by !== currentUserProfileId &&
    !assignedContacts.some(contact => {
      if (contact.source === 'family_member') {
        return contact.profile_id === currentUserProfileId;
      } else {
        return contact.originalData?.profile_id === currentUserProfileId;
      }
    });

  const smartDate = getSmartDateDisplay(task.due_date);

  const getAssignedMemberName = () => {
    if (assignedContacts.length > 0) {
      return assignedContacts[0].displayName;
    }
    return 'quelqu\'un d\'autre';
  };

  const handleCompleteTask = async () => {
    if (needsConfirmation) {
      const assignedName = getAssignedMemberName();
      const confirmed = await showConfirmation({
        title: "Marquer comme terminée",
        message: `Cette tâche est assignée à ${assignedName}. Êtes-vous sûr de vouloir la marquer comme terminée ?`,
        okButtonTitle: "Confirmer",
        cancelButtonTitle: "Annuler"
      });
      
      if (!confirmed) {
        return;
      }
    }
    
    onCompleteTask(task.id);
    onTaskCompleted?.();
  };

  const handleTaskClick = () => {
    navigate(`/tasks/edit/${task.id}`);
  };

  const { handleTouchStart, handleTouchMove, handleTouchEnd, dragX, isCompleting } = useSwipeGesture({
    onSwipeLeft: canComplete && !isDragging ? handleCompleteTask : undefined,
    threshold: 80
  });

  const dragProgress = Math.abs(dragX) / 80;
  const isDraggingLeft = dragX < -10;

  return (
    <div 
      ref={setNodeRef}
      className={`relative w-full border border-panel rounded-lg p-3 bg-panel transition-all duration-200 cursor-pointer overflow-hidden min-h-[2.5rem] ${
        isCompleting ? 'animate-slide-out-left opacity-0' : 'hover:bg-panel-hover'
      } ${
        isDraggingLeft && canComplete 
          ? 'bg-orange-500/10 border-orange-500/30 shadow-orange-500/20' 
          : ''
      } ${
        isDragging ? 'opacity-50 scale-105 rotate-1 shadow-lg z-50' : ''
      } ${
        isDraggable ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
      style={{
        ...style,
        transform: isDraggable 
          ? CSS.Transform.toString(transform)
          : `translateX(${dragX}px)`,
        transition: isDraggable 
          ? transition
          : isCompleting ? 'all 0.3s ease-out' : dragX !== 0 ? 'none' : 'transform 0.2s ease-out'
      }}
      onClick={!isDragging ? handleTaskClick : undefined}
      {...(!isDraggable ? { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd } : {})}
      {...(isDraggable ? attributes : {})}
    >
      {/* Check icon background when dragging left */}
      {isDraggingLeft && canComplete && (
        <div 
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-orange-600"
          style={{ opacity: Math.min(dragProgress, 1) }}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
      
      <div className={`${task.status === 'completed' ? 'opacity-70' : ''}`}>
        <div className="flex items-start justify-between">
          {/* Drag handle for draggable tasks */}
          {isDraggable && (
            <div 
              className="flex items-center justify-center w-6 h-6 mr-2 rounded hover:bg-muted/50 transition-colors cursor-grab active:cursor-grabbing flex-shrink-0"
              {...listeners}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          
          {/* Left: Task content */}
          <div className="flex-1 min-w-0 space-y-1 pr-3">
            <h4 className={`font-medium text-sm leading-tight break-words line-clamp-2 ${
              task.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'
            }`}>
              {task.title}
            </h4>
            
            {/* Date below title with new colors for urgency */}
            {smartDate && (
              <div className="text-xs">
                <span className={`${
                  smartDate === "En retard" || smartDate?.startsWith("Il y a") ? 'text-destructive font-medium' :
                  smartDate === "Aujourd'hui" || smartDate === "Hier" ? 'text-orange-600 font-medium' :
                  smartDate === "Demain" ? 'text-primary' :
                  'text-muted-foreground'
                }`}>
                  {smartDate}
                </span>
              </div>
            )}
          </div>

          {/* Right: Assignee avatars */}
          <div className="flex-shrink-0">
            <MultipleAssigneeAvatars 
              assignedContacts={assignedContacts}
              size="sm"
              maxVisible={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
}